import {
    CENTER_REGION_ID,
    DOCK_ZONES,
    LAYOUT_REGIONS,
    ROOT_BOTTOM_EDGE_PRIORITY_PX,
    ROOT_ZONE_BAR_RATIO,
} from './constants.js';

const MIN_HOST_SIZE = 16;

/**
 * Collect dock host elements for sub-zone overlay (tier: host).
 */
export function collectDockHostsFromDom(bodyEl, getPanelMeta, dragContext = null) {
    if (!bodyEl) return [];

    const hosts = bodyEl.querySelectorAll('[data-dock-host-id]');
    const targets = [];
    const dragPanelId = dragContext?.panelId || null;
    const dragGroupNodeId = dragContext?.groupNodeId || null;

    for (const el of hosts) {
        const nodeId = el.getAttribute('data-dock-host-id');
        if (!nodeId) continue;

        const hostType = el.getAttribute('data-dock-host-type') || 'panel';
        const panelId = el.getAttribute('data-dock-panel-id') || null;
        const rect = el.getBoundingClientRect();

        if (rect.width < MIN_HOST_SIZE || rect.height < MIN_HOST_SIZE) continue;

        if (dragGroupNodeId && nodeId === dragGroupNodeId) continue;
        if (dragPanelId && panelId === dragPanelId && hostType === 'panel') {
            continue;
        }

        let label = el.getAttribute('data-dock-host-label');
        if (!label && panelId && typeof getPanelMeta === 'function') {
            label = getPanelMeta(panelId)?.title || panelId;
        }
        if (!label) {
            if (hostType === 'center') label = 'Center';
            else if (hostType === 'tabs') label = 'Tab group';
            else label = 'Panel';
        }

        targets.push({
            nodeId,
            hostType,
            panelId,
            tier: 'host',
            rect: {
                left: rect.left,
                top: rect.top,
                width: rect.width,
                height: rect.height,
            },
            label,
        });
    }

    return targets;
}

/**
 * Always-visible root edge bars (tier: root) — left, top, right, bottom only.
 */
export function collectRootZoneBars(bodyEl) {
    if (!bodyEl) return [];
    const rect = bodyEl.getBoundingClientRect();
    if (rect.width < MIN_HOST_SIZE || rect.height < MIN_HOST_SIZE) {
        return [];
    }

    const t = Math.max(28, Math.min(rect.width, rect.height) * ROOT_ZONE_BAR_RATIO);
    const r = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
    };

    const bars = [];

    for (const region of LAYOUT_REGIONS) {
        let zoneRect;
        switch (region) {
            case 'left':
                zoneRect = {
                    left: r.left,
                    top: r.top,
                    width: t,
                    height: r.height,
                };
                break;
            case 'right':
                zoneRect = {
                    left: r.left + r.width - t,
                    top: r.top,
                    width: t,
                    height: r.height,
                };
                break;
            case 'top':
                zoneRect = {
                    left: r.left,
                    top: r.top,
                    width: r.width,
                    height: t,
                };
                break;
            case 'bottom':
                zoneRect = {
                    left: r.left,
                    top: r.top + r.height - t,
                    width: r.width,
                    height: t,
                };
                break;
            case 'center':
                zoneRect = {
                    left: r.left + t,
                    top: r.top + t,
                    width: Math.max(0, r.width - t * 2),
                    height: Math.max(0, r.height - t * 2),
                };
                break;
            default:
                continue;
        }

        bars.push({
            nodeId: null,
            hostType: 'root',
            tier: 'root',
            region,
            zone: region,
            panelId: null,
            rect: zoneRect,
            zoneRect,
            label: 'Layout',
            hint: rootZoneHint(region),
        });
    }

    return bars;
}

function rootZoneHint(region) {
    switch (region) {
        case 'left':
            return 'Dock to left band';
        case 'right':
            return 'Dock to right band';
        case 'top':
            return 'Dock to top band';
        case 'bottom':
            return 'Dock to bottom band';
        case 'center':
            return 'Dock to center region';
        default:
            return 'Dock to layout';
    }
}

export function zoneRectWithinHost(rect, zone) {
    const w = rect.width;
    const h = rect.height;
    const qw = w * 0.25;
    const qh = h * 0.25;

    switch (zone) {
        case 'left':
            return {
                left: rect.left,
                top: rect.top,
                width: qw,
                height: h,
            };
        case 'right':
            return {
                left: rect.left + w - qw,
                top: rect.top,
                width: qw,
                height: h,
            };
        case 'top':
            return {
                left: rect.left,
                top: rect.top,
                width: w,
                height: qh,
            };
        case 'bottom':
            return {
                left: rect.left,
                top: rect.top + h - qh,
                width: w,
                height: qh,
            };
        case 'center':
            return {
                left: rect.left,
                top: rect.top,
                width: w,
                height: h,
            };
        default:
            return rect;
    }
}

export function zoneDropHint(zone, host) {
    const name = host?.label || 'panel';
    switch (zone) {
        case 'center':
            if (host?.hostType === 'tabs' || host?.hostType === 'center') {
                return `Add as tab in ${name}`;
            }
            return `Tab with ${name}`;
        case 'left':
            return `Dock left of ${name}`;
        case 'right':
            return `Dock right of ${name}`;
        case 'top':
            return `Dock above ${name}`;
        case 'bottom':
            return `Dock below ${name}`;
        default:
            return name;
    }
}

/**
 * Build full overlay zone list: root bars (always) + host sub-zones.
 * @param {(zone: string, hit: object) => boolean} [canDockInZone] — return false to mark zone as not allowed
 */
export function buildDropZoneList(
    bodyEl,
    getPanelMeta,
    dragContext = null,
    canDockInZone = null,
) {
    const zones = [];
    const allow = (zone, hit) => {
        if (typeof canDockInZone !== 'function') return true;
        return canDockInZone(zone, hit);
    };

    for (const bar of collectRootZoneBars(bodyEl)) {
        zones.push({
            ...bar,
            allowed: allow(bar.zone, bar),
        });
    }

    const hosts = collectDockHostsFromDom(bodyEl, getPanelMeta, dragContext);
    for (const host of hosts) {
        for (const zone of DOCK_ZONES) {
            const hit = {
                ...host,
                zone,
                zoneRect: zoneRectWithinHost(host.rect, zone),
                hint: zoneDropHint(zone, host),
            };
            zones.push({
                ...hit,
                allowed: allow(zone, hit),
            });
        }
    }

    return zones;
}

function pointInZoneRect(r, clientX, clientY) {
    return (
        clientX >= r.left &&
        clientX <= r.left + r.width &&
        clientY >= r.top &&
        clientY <= r.top + r.height
    );
}

function zoneHoverKey(z) {
    if (!z) return null;
    if (z.tier === 'root') return `root::${z.region}`;
    return `${z.nodeId}::${z.zone}`;
}

function smallestAreaHostHit(hostHits) {
    return hostHits.reduce((best, z) => {
        const area = z.zoneRect.width * z.zoneRect.height;
        const bestArea = best.zoneRect.width * best.zoneRect.height;
        return area < bestArea ? z : best;
    });
}

/**
 * When the pointer is within ROOT_BOTTOM_EDGE_PRIORITY_PX of the dock body bottom,
 * prefer the top-level bottom layout region over host "dock below panel" zones.
 */
export function pickRootBottomEdgeOverride(bodyRect, clientX, clientY, rootHits) {
    if (!bodyRect || !rootHits?.length) return null;
    const bodyBottom = bodyRect.top + bodyRect.height;
    const distFromBottom = bodyBottom - clientY;
    if (
        distFromBottom < 0 ||
        distFromBottom > ROOT_BOTTOM_EDGE_PRIORITY_PX
    ) {
        return null;
    }
    return (
        rootHits.find(
            (z) =>
                z.tier === 'root' &&
                z.region === 'bottom' &&
                z.zone === 'bottom',
        ) || null
    );
}

/**
 * Host sub-zones beat root bars when pointer is over a host, except near the
 * dock body bottom edge where the root bottom band wins (see pickRootBottomEdgeOverride).
 * @param {{ allowedOnly?: boolean, preferredHoverKey?: string | null, bodyRect?: DOMRect | { top: number, left: number, width: number, height: number } | null }} [opts]
 */
export function hitTestDropZone(zones, clientX, clientY, opts = {}) {
    const { allowedOnly = true, preferredHoverKey = null, bodyRect = null } =
        opts;
    const hostHits = [];
    const rootHits = [];

    for (const z of zones) {
        if (allowedOnly && z.allowed === false) continue;
        const r = z.zoneRect;
        if (!r || !pointInZoneRect(r, clientX, clientY)) continue;
        if (z.tier === 'host') hostHits.push(z);
        else rootHits.push(z);
    }

    if (preferredHoverKey) {
        const allHits = [...hostHits, ...rootHits];
        const preferred = allHits.find((z) => zoneHoverKey(z) === preferredHoverKey);
        if (preferred) return preferred;
    }

    if (hostHits.length) {
        const rootBottomOverride = pickRootBottomEdgeOverride(
            bodyRect,
            clientX,
            clientY,
            rootHits,
        );
        if (rootBottomOverride) {
            const overrideKey = zoneHoverKey(rootBottomOverride);
            if (
                (!preferredHoverKey ||
                    preferredHoverKey === overrideKey) &&
                (!allowedOnly || rootBottomOverride.allowed !== false)
            ) {
                return rootBottomOverride;
            }
        }

        const best = smallestAreaHostHit(hostHits);
        if (preferredHoverKey) {
            const preferred = hostHits.filter(
                (z) => zoneHoverKey(z) === preferredHoverKey,
            );
            if (preferred.length) {
                const preferredBest = smallestAreaHostHit(preferred);
                if (zoneHoverKey(preferredBest) === zoneHoverKey(best)) {
                    return preferredBest;
                }
            }
        }
        return best;
    }

    if (rootHits.length) {
        if (preferredHoverKey) {
            const preferred = rootHits.filter(
                (z) => zoneHoverKey(z) === preferredHoverKey,
            );
            if (preferred.length) return preferred[0];
        }
        return rootHits[0];
    }

    return null;
}

export { CENTER_REGION_ID };
